import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { CircularProgress } from '@mui/material'
import { Instance } from 'mobx-state-tree'

import { TagApi } from '@trivie/core/src/services/api-objects'
import { TagSelector } from '@trivie/ui-web/components/tag-selector'
import { Text } from '@trivie/ui-web/components/text'
import { SubscriptionProduct } from '@trivie/core/src/models/subscriptions/SubscriptionProduct'

interface SubscriptionTagsProps {
  form: Instance<typeof SubscriptionProduct>
  editingProduct?: any
}

export const SubscriptionTags: React.FC<SubscriptionTagsProps> = observer(
  ({ form, editingProduct }) => {
    const [loading, setLoading] = useState(true)
    const [tags, setTags] = useState<any[]>(() => ((form.tag_ids as any[]) ?? []).slice())
    const [availableTags, setAvailableTags] = useState<any[]>([])
    const [formattedTags, setFormattedTags] = useState({})

    const init = (preselect?: any) => {
      TagApi.getAllTags(
        'tags',
        false,
        [{ field: 'tag_key' }, { field: 'tag_value' }],
        'list_tags',
      ).then((resp) => {
        if (resp.ok) {
          const available = (resp as any).data.rows.map((tag) => {
            const key = tag.columns.find((column) => column.field === 'tag_key')
            const value = tag.columns.find((column) => column.field === 'tag_value')
            return { id: tag.id, key: key.value, value: value.value }
          })
          const t: Record<string, { values: any[] }> = {}
          setAvailableTags(available)
          available.forEach((tag) => {
            if (!t[tag.key]) {
              t[tag.key] = { values: [] }
            }
            t[tag.key].values.push(tag)
          })

          if (preselect) {
            const newTag = available.find(
              (availableTag) =>
                availableTag.key === preselect?.key && availableTag.value === preselect?.value,
            )
            if (newTag) {
              setTags((prev) => [...prev, newTag])
            }
          }

          if (editingProduct?.tag_ids?.length && !preselect) {
            const editTagIds = editingProduct.tag_ids
            const matched = available.filter((a: any) => editTagIds.includes(a.id))
            if (matched.length > 0) {
              setTags(matched)
            }
          }

          setFormattedTags(t)
          setLoading(false)
        }
      })
    }

    useEffect(() => {
      init()
    }, [])

    useEffect(() => {
      form.set('tag_ids', tags as any)
    }, [tags])

    if (loading) {
      return (
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </div>
      )
    }

    return (
      <div
        style={{ gap: 12, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Text
          component="div"
          text="Tags let you label and segment users by shared attributes such as timeframe or subscription, making it easy to filter and target specific groups."
        />
        <Text component="div" text="Tags are not required. You can add them at any time!" />
        <TagSelector
          open
          subscription
          formattedTags={formattedTags}
          tags={availableTags}
          allowCreateNew
          onCreateNew={(payload) => init(payload)}
          selectedTags={tags}
          onChange={(selected) => setTags(selected)}
        />
      </div>
    )
  },
)
